import { useState, useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { trackClientEvent } from 'analytics/analytics'
import TextArea from 'components/Common/Form/Input/TextArea'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import DividerWithText from 'components/Common/DividerWithText'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import TileCard from '../EntryPoints/Common/Tile/TileCard'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import Sheet from 'components/utils/Sheet'
import type { TripItem } from 'tripPlanner/types/tripItem'

interface RegenerateDayModalProps {
  tripId: string
  startItems: Array<TripItem>
  onRegenerateDaySubmit: (userInput?: string) => void
  onAlternativeGroupSubmit: (alternativeGroup: string) => void
}

function RegenerateDayModal({
  tripId,
  onRegenerateDaySubmit,
  onAlternativeGroupSubmit,
  startItems,
}: RegenerateDayModalProps) {
  const intl = useIntl()
  const { data: trip } = useTrip({ tripId })
  const modalElement = useModalElementContext()
  const [userInput, setUserInput] = useState<string | undefined>(undefined)

  const alternativeGroups = useMemo(() => {
    if (!trip?.items) return []

    const alternativeGroupItems = trip.items.filter(item =>
      item.alternativeGroup &&
      !item.startDate,
    )

    return Object.keys(
      Object.groupBy(alternativeGroupItems, (item) => item.alternativeGroup!),
    )
  }, [trip])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(e.target.value)
  }, [])

  const handleAlternativeGroupClick = useCallback((group: string) => {
    trackClientEvent({
      subject: 'trip_regenerate_day_request_with_alternative_group',
      action: tripId,
      category: `alternative_group_${group}`,
      type: 'interaction',
    })

    onAlternativeGroupSubmit(group)
    modalElement.resolve() // Close modal immediately
  }, [modalElement, onAlternativeGroupSubmit, tripId])

  const handleSubmit = useCallback(() => {
    trackClientEvent({
      subject: 'trip_regenerate_day_request',
      action: tripId,
      category: userInput ?? 'no user input',
      type: 'interaction',
    })

    onRegenerateDaySubmit(userInput || undefined)
    setUserInput(undefined)
    modalElement.resolve()
  }, [modalElement, onRegenerateDaySubmit, userInput, tripId])

  const handleCancel = useCallback(() => {
    setUserInput(undefined)
    modalElement.resolve()
  }, [modalElement])

  const parsedAlternativeGroups = useMemo(() => {
    return alternativeGroups.map((group) => {
      const parts = group.split(' | ')
      if (parts.length < 3) {
        return null
      }
      return {
        key: group,
        icon: parts[0] || '',
        title: parts[1] || group,
        subtitle: parts.slice(2).join(' | ') || '',
      }
    })
  }, [alternativeGroups])
  let title
  let subtitle
  if (startItems.length > 0) {
    title = intl.formatMessage({
      id: 'U0J-rxHLxsv2eP-lByQS',
      defaultMessage: 'Let’s replan your day',
      description: 'Modal title when replanning an existing day in trip planner',
    })
    subtitle = intl.formatMessage({
      id: 'oxgY8LEGyL2nS2hgI4GX',
      defaultMessage: 'Tell us what you’d like to change',
      description: 'Modal subtitle prompting user to describe desired changes',
    })
  } else {
    title = intl.formatMessage({
      id: 'Wyyrfbyuz8Kv89RbI3LJ',
      defaultMessage: 'We’ll use AI to suggest places',
      description: 'Modal title text displayed when generating a new day plan using AI in trip planner',
    })
    subtitle = intl.formatMessage({
      id: '-YYrhZ0cj-FwXF2zFbX-',
      defaultMessage: 'Tell us where you’re going and any ideas you have',
      description: 'Modal subtitle prompting user for destination and preferences',
    })
  }
  return (
    <ModalBase
      onClose={handleCancel}
      size="L"
      height="full"
      tabletHeight="auto"
    >
      <ModalHeader
        title={title}

      />
      <ModalBody>
        <ModalContent>
          {parsedAlternativeGroups.length > 0 && (
            <Sheet padding="24 0">
              <Group direction="vertical" gap={16} verticalAlign="center" horizontalAlign="start">
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <HeadingTextBlock variant="heading5">
                    {intl.formatMessage({
                      id: 'wvKSlmeIQmenl0oo-z2E',
                      defaultMessage: 'Pick a suggested plan',
                      description: 'Heading for alternative AI-generated day plan options',
                    })}
                  </HeadingTextBlock>
                  {startItems.length > 0 && <BodyText variant="medium">
                    {intl.formatMessage({
                      id: 'LAE1AV8-IakDeBsOZP-k',
                      defaultMessage: '(optional)',
                      description: 'Label indicating the suggested plan selection is optional',
                    })}
                  </BodyText>}
                </Group>
                <Group direction="vertical" gap={16} fullWidth>
                  {parsedAlternativeGroups.filter(group => group !== null).map(({ key, icon, title, subtitle }) => (
                    <TileCard
                      key={key}
                      title={title}
                      value={key}
                      subtitle={subtitle}
                      icon={icon}
                      layout="horizontal"
                      onClick={handleAlternativeGroupClick}
                      withOutBackground
                    />
                  ))}
                </Group>
              </Group>
            </Sheet>
          )}
          <Group direction="vertical" gap={12}>
            {parsedAlternativeGroups.length > 0 && <DividerWithText text={intl.formatMessage({
              id: 'cZBpICfql_PVElEyyAMe',
              defaultMessage: 'OR',
              description: 'Divider text between suggested plans and custom input sections',
            })}
            />}
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <HeadingTextBlock variant="heading5">{subtitle}</HeadingTextBlock>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'I5dmWM_ISzvw7cJIEPtK',
                  defaultMessage: '(optional)',
                  description: 'Label indicating that the custom text input field is optional in the regenerate day modal',
                })}
              </BodyText>
            </Group>
            <TextArea
              placeholder={intl.formatMessage({
                id: 'hp1ATiR0B8Rd82tqVuDx',
                defaultMessage: 'The more specific you are, the better we can tailor your perfect day.',
                description: 'Placeholder text for custom day plan input textarea',
              })}
              value={userInput ?? ''}
              onChange={handleInputChange}
            />
          </Group>
        </ModalContent>
      </ModalBody>

      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'ohSJ9cG8kJUAi57IX8HJ',
            defaultMessage: 'Replan this day',
            description: 'Action button to submit day replanning request with AI',
          }),
          onClick: handleSubmit,
          startIcon: <SolidSparklesIcon />,
        }}
      />
    </ModalBase>
  )
}

export default RegenerateDayModal
